// Praten met de server. Eén plek, zodat de rest van de app niets van fetch hoeft te weten —
// en zodat "verlopen sessie" en "geen verbinding" hier uit elkaar gehouden worden.

export type Blok = {
  type: string;
  attrs?: Record<string, unknown>;
  content?: Blok[];
  marks?: { type: string; attrs?: Record<string, unknown> }[];
  text?: string;
};

export type Taken = { af: number; totaal: number };

export type Notitie = {
  id: string;
  soort: "notitie" | "notitieboek";
  ouder: string | null;
  titel: string;
  inhoud: Blok | null;
  gepind: boolean;
  kleur: string | null;
  sortering: string;
  systeem: string | null;
  dag: string | null;
  rev: number;
  aangemaakt: string;
  gewijzigd: string;
  verwijderd: string | null;
  gearchiveerd: string | null;
  taken: Taken;
  tags: string[];
  eigenschappen: Record<string, string>;
  kluis: boolean;
  sjabloon: boolean;
};

/** Eén regel uit de versiegeschiedenis; de inhoud zelf halen we pas op als je hem kiest. */
export type VersieRegel = {
  nr: number;
  rev: number;
  titel: string;
  gemaakt: string;
  fragment: string;
};

export type Versie = VersieRegel & { inhoud: Blok | null };

export type Rij = {
  id: string;
  titel: string;
  fragment: string;
  gepind: boolean;
  kleur: string | null;
  ouder: string | null;
  gewijzigd: string;
  aangemaakt: string;
  taken: Taken;
  tags: string[];
  eigenschappen: Record<string, string>;
  kluis: boolean;
};

export type Boek = {
  id: string;
  titel: string;
  ouder: string | null;
  systeem: string | null;
  aantal: number;
  kinderen: Boek[];
};

export type Treffer = Rij & {
  score: number;
  /** Gevonden in de tekst van een bijlage (een schermafdruk, een pdf) en niet in de notitie zelf. */
  viaBijlage?: string;
};

export type ImportUitslag = {
  bestanden: number;
  nieuw: number;
  bijgewerkt: number;
  overgeslagen: number;
  boeken: number;
  meldingen: string[];
};

export type BestandRegel = {
  hash: string;
  naam: string;
  url: string;
  mime: string;
  grootte: number;
  aangemaakt: string;
  afbeelding: boolean;
  gebruiktIn: { id: string; titel: string }[];
  /** De stand van de tekstherkenning; `null` als dit soort bestand niet te lezen valt. */
  tekst: { stand: "wacht" | "klaar" | "mislukt" | "overgeslagen"; tekens: number } | null;
};

export type TagRegel = { naam: string; aantal: number };
export type EigenschapSoort = "datum" | "getal" | "vink" | "tekst";
export type KluisStand = { ingesteld: boolean; zout: string; rondes: number; controle: string; notities: number };
export type EigenschapRegel = { sleutel: string; aantal: number; soort: EigenschapSoort; waarden: string[] };
export type Backlink = { id: string; titel: string; fragment: string; gewijzigd: string };
export type Zoekopdracht = {
  id: string;
  naam: string;
  vraag: string;
  volgorde: string;
  aangemaakt: string;
  weergave: string | null;
  groepeer: string | null;
};
export type TokenRegel = { id: string; naam: string; aangemaakt: string; laatstGebruikt: string | null };

export type ClipUitslag = { notitie: Notitie; afbeeldingen: number; bron: string };

export type Taak = {
  notitie: string;
  titel: string;
  nr: number;
  tekst: string;
  af: boolean;
  diepte: number;
  wanneer: string | null;
  gewijzigd: string;
  boek: string | null;
};

/** De Authentik-sessie is verlopen; alleen een echte paginanavigatie helpt nog. */
export class NietAangemeld extends Error {
  constructor() {
    super("niet aangemeld");
    this.name = "NietAangemeld";
  }
}

/** Geen netwerk (of de NAS is even weg). Werk blijft in de wachtrij staan. */
export class GeenVerbinding extends Error {
  constructor() {
    super("geen verbinding");
    this.name = "GeenVerbinding";
  }
}

/** De server kent deze notitie niet (meer). */
export class NietGevonden extends Error {
  constructor() {
    super("niet gevonden");
    this.name = "NietGevonden";
  }
}

/** Een ander apparaat was eerder; `actueel` is wat er nu op de server staat. */
export class Botsing extends Error {
  actueel: Notitie;
  constructor(actueel: Notitie) {
    super("botsing");
    this.name = "Botsing";
    this.actueel = actueel;
  }
}

async function vraag<T>(pad: string, opties: RequestInit = {}): Promise<T> {
  let antwoord: Response;
  // Alleen een content-type als er ook iets in het verzoek zit. Stuurden we hem altijd mee,
  // dan kreeg een verzoek zónder lichaam — een notitie weggooien, een notitie herstellen —
  // van Fastify een 400: "Body cannot be empty when content-type is set to
  // 'application/json'". Het weggooien bleef zo in de wachtrij hangen en na een verversing
  // stond de notitie er gewoon weer.
  const metLichaam = opties.body !== undefined && opties.body !== null;
  try {
    antwoord = await fetch(pad, {
      ...opties,
      // Zónder dit volgt de browser de doorverwijzing van Authentik naar de inlogpagina van
      // een ander domein, en komt er een onbegrijpelijke CORS-fout terug die niet te
      // onderscheiden is van "offline". Nu zien we de doorverwijzing zelf.
      redirect: "manual",
      headers: { ...(metLichaam ? { "content-type": "application/json" } : {}), ...(opties.headers ?? {}) }
    });
  } catch {
    throw new GeenVerbinding();
  }

  if (antwoord.type === "opaqueredirect" || antwoord.status === 401) throw new NietAangemeld();

  if (antwoord.status === 404) throw new NietGevonden();

  if (antwoord.status === 409) {
    const lading = await antwoord.json();
    throw new Botsing(lading.actueel as Notitie);
  }
  if (!antwoord.ok) {
    const tekst = await antwoord.text().catch(() => "");
    // De server legt in `fout` uit wát er mis is, in een zin die voor de gebruiker bedoeld is
    // ("de pagina gaf 403"). Die hoort niet weg te vallen achter "server gaf 400: {…}": bij
    // het knippen van een pagina is juist die zin het hele antwoord.
    let reden = `server gaf ${antwoord.status}: ${tekst.slice(0, 200)}`;
    try {
      const lading = JSON.parse(tekst) as { fout?: unknown; message?: unknown };
      const uitleg = typeof lading.fout === "string" ? lading.fout : typeof lading.message === "string" ? lading.message : "";
      if (uitleg) reden = uitleg;
    } catch {
      // Geen json: dan is de platte tekst het beste wat we hebben.
    }
    const fout = new Error(reden) as Error & { status?: number };
    fout.status = antwoord.status;
    throw fout;
  }
  if (antwoord.status === 204) return undefined as T;
  return (await antwoord.json()) as T;
}

export const api = {
  ik: () => vraag<{ gebruiker: { id: string; naam: string; email: string | null }; inbox: string; vandaag: string }>("/api/ik"),

  boom: () => vraag<{ boeken: Boek[] }>("/api/boom"),

  lijst: (opties: { boek?: string; tag?: string; sorteer?: string; limiet?: number; eig?: string; eigwaarde?: string } = {}) => {
    const vraagTekst = new URLSearchParams();
    if (opties.boek) vraagTekst.set("boek", opties.boek);
    if (opties.tag) vraagTekst.set("tag", opties.tag);
    if (opties.eig) vraagTekst.set("eig", opties.eig);
    if (opties.eigwaarde) vraagTekst.set("eigwaarde", opties.eigwaarde);
    if (opties.sorteer) vraagTekst.set("sorteer", opties.sorteer);
    if (opties.limiet) vraagTekst.set("limiet", String(opties.limiet));
    const achtervoegsel = vraagTekst.toString();
    return vraag<{ rijen: Rij[]; totaal: number }>(`/api/notities${achtervoegsel ? "?" + achtervoegsel : ""}`);
  },

  haal: (id: string) => vraag<Notitie>(`/api/notities/${id}`),

  sjablonen: () => vraag<{ rijen: Rij[]; totaal: number }>("/api/sjablonen"),

  maak: (nieuw: { id?: string; titel?: string; inhoud?: Blok | null; boek?: string | null }) =>
    vraag<Notitie>("/api/notities", { method: "POST", body: JSON.stringify(nieuw) }),

  maakBoek: (titel: string, boek?: string | null) =>
    vraag<Notitie>("/api/notitieboeken", { method: "POST", body: JSON.stringify({ titel, boek }) }),

  wijzig: (id: string, patch: Record<string, unknown>, rev?: number) =>
    vraag<Notitie>(`/api/notities/${id}`, { method: "PATCH", body: JSON.stringify({ ...patch, rev }) }),

  verwijder: (id: string) => vraag<Notitie>(`/api/notities/${id}`, { method: "DELETE" }),

  herstel: (id: string) => vraag<Notitie>(`/api/notities/${id}/herstel`, { method: "POST" }),

  prullenbak: () => vraag<{ rijen: Rij[] }>("/api/prullenbak"),

  /** Wat er sinds volgnummer `sinds` op de server is veranderd — ook van een ander apparaat. */
  wijzigingen: (sinds: number) =>
    vraag<{ volgnr: number; notities: Notitie[]; verwijderd: string[] }>(`/api/wijzigingen?sinds=${sinds}`),

  /** Alles wat de browser lokaal bewaart, in stukken; `na` is het laatste id van de vorige ronde. */
  spiegel: (na?: string, limiet = 200) =>
    vraag<{ notities: Notitie[]; volgnr: number; klaar: boolean }>(
      `/api/spiegel?limiet=${limiet}${na ? `&na=${encodeURIComponent(na)}` : ""}`
    ),

  /** Een eerdere export weer inlezen. Het bestand gaat als bytes de deur uit, niet als json. */
  importeer: (bestand: Blob, overschrijven = false) =>
    vraag<ImportUitslag>(`/api/import${overschrijven ? "?overschrijven=1" : ""}`, {
      method: "POST",
      body: bestand,
      headers: { "content-type": "application/zip" }
    }),

  /** Alle bijlagen, met de notities waarin ze voorkomen. */
  bestanden: () => vraag<{ bestanden: BestandRegel[] }>("/api/bestanden"),

  versies: (id: string) => vraag<{ versies: VersieRegel[] }>(`/api/notities/${id}/versies`),

  versie: (id: string, nr: number) => vraag<Versie>(`/api/notities/${id}/versies/${nr}`),

  /** Zet een oude versie terug. De huidige stand wordt eerst zelf een versie. */
  zetVersieTerug: (id: string, nr: number) =>
    vraag<Notitie>(`/api/notities/${id}/versies/${nr}/herstel`, { method: "POST" }),

  zoek: (q: string) =>
    vraag<{ treffers: Treffer[]; duurMs: number }>(`/api/zoek?q=${encodeURIComponent(q)}`),

  vandaag: () => vraag<Notitie>("/api/vandaag"),

  vastleggen: (lading: { titel?: string; tekst?: string; url?: string; boek?: string | null }) =>
    vraag<Notitie>("/api/vastleggen", { method: "POST", body: JSON.stringify(lading) }),

  tags: () => vraag<{ tags: TagRegel[] }>("/api/tags"),

  hernoemTag: (oud: string, nieuw: string) =>
    vraag<{ geraakt: number; tags: TagRegel[] }>("/api/tags/hernoem", {
      method: "POST",
      body: JSON.stringify({ oud, nieuw })
    }),

  backlinks: (id: string) => vraag<{ backlinks: Backlink[] }>(`/api/notities/${id}/backlinks`),

  zoekopdrachten: () => vraag<{ zoekopdrachten: Zoekopdracht[] }>("/api/zoekopdrachten"),

  bewaarZoekopdracht: (naam: string, vraagTekst: string, kijk: { weergave?: string; groepeer?: string } = {}) =>
    vraag<Zoekopdracht>("/api/zoekopdrachten", {
      method: "POST",
      body: JSON.stringify({ naam, vraag: vraagTekst, ...kijk })
    }),

  verwijderZoekopdracht: (id: string) =>
    vraag<{ zoekopdrachten: Zoekopdracht[] }>(`/api/zoekopdrachten/${id}`, { method: "DELETE" }),

  tokens: () => vraag<{ tokens: TokenRegel[] }>("/api/tokens"),

  maakToken: (naam: string) =>
    vraag<{ token: string; regel: TokenRegel }>("/api/tokens", { method: "POST", body: JSON.stringify({ naam }) }),

  trekTokenIn: (id: string) => vraag<{ tokens: TokenRegel[] }>(`/api/tokens/${id}`, { method: "DELETE" }),

  kluis: () => vraag<KluisStand>("/api/kluis"),

  richtKluisIn: (zout: string, rondes: number, controle: string, opnieuw = false) =>
    vraag<KluisStand>("/api/kluis", { method: "POST", body: JSON.stringify({ zout, rondes, controle, opnieuw }) }),

  pushSleutel: () => vraag<{ sleutel: string; apparaten: { id: string; naam: string }[] }>("/api/push/sleutel"),

  meldAanVoorPush: (abonnement: unknown, naam: string) =>
    vraag<{ id: string; apparaten: { id: string; naam: string }[] }>("/api/push/abonnement", {
      method: "POST",
      body: JSON.stringify({ abonnement, naam })
    }),

  meldAfVoorPush: (endpoint: string) =>
    vraag<{ apparaten: { id: string; naam: string }[] }>("/api/push/abonnement", {
      method: "DELETE",
      body: JSON.stringify({ endpoint })
    }),

  proefMelding: () => vraag<{ aangekomen: number }>("/api/push/proef", { method: "POST" }),

  eigenschappen: () => vraag<{ sleutels: EigenschapRegel[] }>("/api/eigenschappen"),

  zetEigenschappen: (id: string, eigenschappen: Record<string, string>) =>
    vraag<Notitie>(`/api/notities/${id}/eigenschappen`, { method: "PUT", body: JSON.stringify({ eigenschappen }) }),

  taken: (af?: boolean) =>
    vraag<{ taken: Taak[]; open: number }>(`/api/taken${af === undefined ? "" : `?af=${af ? 1 : 0}`}`),

  /** Een vinkje zetten zonder de notitie te openen; `tekst` is wat er in het overzicht stond. */
  zetTaakAf: (notitie: string, nr: number, af: boolean, tekst: string) =>
    vraag<{ notitie: Notitie; tekst: string }>(`/api/taken/${notitie}/${nr}`, {
      method: "POST",
      body: JSON.stringify({ af, tekst })
    }),

  /** Een webpagina als notitie bewaren. Zonder `html` haalt de NAS de pagina zelf op. */
  clip: (invoer: { url: string; titel?: string; html?: string; boek?: string | null }) =>
    vraag<ClipUitslag>("/api/clip", { method: "POST", body: JSON.stringify(invoer) })
};
